import React, { useCallback, useEffect, useState } from "react";
import axios from "../api";
import {
  getCurrentCoordinates,
  getStoredRole,
  storeOfficeAccessPolicy,
} from "../utils/officeAccess";
import { normalizePermissions } from "../utils/permissions";

const clearSessionAndReturnToLogin = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("username");
  localStorage.removeItem("userRole");
  localStorage.removeItem("userPermissions");
  window.location.assign("/");
};

const OfficeAccessGuard = ({ children }) => {
  const role = getStoredRole();
  const [status, setStatus] = useState(
    role === "admin" || role === "superadmin" ? "allowed" : "checking"
  );
  const [message, setMessage] = useState("");

  const verifyAccess = useCallback(async () => {
    setMessage("");

    try {
      const sessionResponse = await axios.get("/admin/session");
      if (sessionResponse.data?.user) {
        localStorage.setItem(
          "username",
          String(sessionResponse.data.user.username || "")
        );
        localStorage.setItem(
          "userRole",
          String(sessionResponse.data.user.role || "")
        );
      }
      if (sessionResponse.data?.permissions) {
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(normalizePermissions(sessionResponse.data.permissions))
        );
        window.dispatchEvent(new CustomEvent("admin-session-updated"));
      }

      if (role === "admin" || role === "superadmin" || role !== "user") {
        setStatus("allowed");
        return;
      }

      const policyResponse = await axios.get("/admin/access-policy");
      const policy = policyResponse.data?.policy;
      storeOfficeAccessPolicy(policy);

      if (!policy?.enabled) {
        setStatus("allowed");
        return;
      }

      const coordinates = await getCurrentCoordinates();
      const verification = await axios.post(
        "/admin/access-policy/verify",
        coordinates
      );

      if (verification.data?.allowed) {
        setStatus("allowed");
        return;
      }

      setMessage(
        verification.data?.message ||
          "Dashboard access is restricted outside the office."
      );
      setStatus("blocked");
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          error.message ||
          "Dashboard access is restricted outside the office."
      );
      setStatus("blocked");
    }
  }, [role]);

  useEffect(() => {
    verifyAccess();
    const intervalId = window.setInterval(() => {
      verifyAccess();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [verifyAccess]);

  if (status === "allowed") return children;

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-yellow-400 border-t-transparent" />
          <p className="mt-4 font-medium text-slate-700">
            Verifying office location...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-2xl">
        <h2 className="text-xl font-bold text-red-600">Access Restricted</h2>
        <p className="mt-3 text-slate-700">{message}</p>
        <p className="mt-2 text-sm text-slate-500">
          User accounts can access the dashboard only within 50 meters of the
          office while Location access is on.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={verifyAccess}
            className="border border-yellow-400 px-4 py-2 font-semibold hover:bg-yellow-50"
          >
            Retry Location
          </button>
          <button
            type="button"
            onClick={clearSessionAndReturnToLogin}
            className="bg-slate-800 px-4 py-2 font-semibold text-white hover:bg-slate-900"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfficeAccessGuard;
